import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';

import { TopNavBar } from './shared/TopNavBar';
import { useAuth } from './AuthContext';
import { API_BASE } from './config';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: string;
  points: number;
  is_admin: boolean;
  is_active: boolean;
  is_locked: boolean;
  email_verified: boolean;
  google_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface QuickRegisterResult extends AdminUser {
  password: string;
}

const ROLES = ['free', 'creator', 'business'];

const blankCreate = {
  username: '',
  email: '',
  password: '',
  role: 'free',
  points: 0,
  is_admin: false,
};

const AdminPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...blankCreate });
  const [creating, setCreating] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState<Partial<AdminUser> & { password?: string }>({});
  const [saving, setSaving] = useState(false);

  const [quickResult, setQuickResult] = useState<QuickRegisterResult | null>(null);
  const [quickLoading, setQuickLoading] = useState(false);

  const token = useMemo(() => localStorage.getItem('access_token') || '', []);

  // 鉴权门槛：未登录或非管理员 → 跳走
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/home');
    }
  }, [user, authLoading, navigate]);

  const fetchUsers = useCallback(
    async (q?: string) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/api/admin/users`);
        if (q) url.searchParams.set('q', q);
        url.searchParams.set('limit', '200');
        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || '加载失败');
        setUsers(data.items || []);
        setTotal(data.total || 0);
      } catch (e: any) {
        setError(e?.message || '加载失败');
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (user?.is_admin) {
      void fetchUsers();
    }
  }, [user?.is_admin, fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void fetchUsers(search.trim());
  };

  const openEdit = (u: AdminUser) => {
    setEditing(u);
    setEditForm({
      username: u.username,
      email: u.email,
      role: u.role,
      points: u.points,
      is_admin: u.is_admin,
      is_active: u.is_active,
      is_locked: u.is_locked,
      email_verified: u.email_verified,
      password: '',
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { ...editForm };
      if (!body.password) delete body.password;
      const res = await fetch(`${API_BASE}/api/admin/users/${editing.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || '保存失败');
      setSnack({ msg: '已保存', severity: 'success' });
      setEditOpen(false);
      setEditing(null);
      await fetchUsers(search.trim());
    } catch (e: any) {
      setSnack({ msg: e?.message || '保存失败', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const submitCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || '创建失败');
      setSnack({ msg: '用户已创建', severity: 'success' });
      setCreateOpen(false);
      setCreateForm({ ...blankCreate });
      await fetchUsers(search.trim());
    } catch (e: any) {
      setSnack({ msg: e?.message || '创建失败', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (u: AdminUser) => {
    if (!window.confirm(`确认删除用户 ${u.username}（${u.email}）？该操作不可撤销。`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${u.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || '删除失败');
      setSnack({ msg: '已删除', severity: 'success' });
      await fetchUsers(search.trim());
    } catch (e: any) {
      setSnack({ msg: e?.message || '删除失败', severity: 'error' });
    }
  };

  const handleQuickRegister = async () => {
    setQuickLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/quick-register`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || '快捷注册失败');
      setQuickResult(data as QuickRegisterResult);
      await fetchUsers(search.trim());
    } catch (e: any) {
      setSnack({ msg: e?.message || '快捷注册失败', severity: 'error' });
    } finally {
      setQuickLoading(false);
    }
  };

  const copyToClipboard = (txt: string) => {
    try {
      navigator.clipboard?.writeText(txt);
      setSnack({ msg: '已复制', severity: 'info' });
    } catch {
      setSnack({ msg: '复制失败', severity: 'error' });
    }
  };

  if (authLoading || !user?.is_admin) {
    return (
      <Box>
        <TopNavBar />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <TopNavBar />
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: 3, py: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
            用户管理 <Typography component="span" sx={{ color: '#64748b', fontSize: '0.9rem', ml: 1 }}>共 {total} 名用户</Typography>
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<BoltIcon />}
              onClick={handleQuickRegister}
              disabled={quickLoading}
            >
              {quickLoading ? '生成中...' : '快捷注册用户'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              新建用户
            </Button>
            <Tooltip title="刷新">
              <IconButton onClick={() => fetchUsers(search.trim())}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
          <form onSubmit={handleSearch}>
            <Stack direction="row" gap={1}>
              <TextField
                size="small"
                placeholder="按用户名或邮箱搜索"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
              />
              <Button type="submit" variant="outlined">
                搜索
              </Button>
              {search && (
                <Button
                  variant="text"
                  onClick={() => {
                    setSearch('');
                    void fetchUsers();
                  }}
                >
                  清空
                </Button>
              )}
            </Stack>
          </form>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>用户名</TableCell>
                <TableCell>邮箱</TableCell>
                <TableCell>角色</TableCell>
                <TableCell align="right">积分</TableCell>
                <TableCell>状态</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: '#94a3b8', py: 4 }}>
                    暂无数据
                  </TableCell>
                </TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Stack direction="row" gap={0.5} alignItems="center">
                      <Typography sx={{ fontWeight: 600 }}>{u.username}</Typography>
                      {u.is_admin && (
                        <Chip size="small" color="primary" label="ADMIN" sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: '#475569' }}>{u.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={u.role || 'free'}
                      color={u.role === 'business' ? 'success' : u.role === 'creator' ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">{u.points}</TableCell>
                  <TableCell>
                    <Stack direction="row" gap={0.5}>
                      {!u.is_active && <Chip size="small" label="未激活" color="warning" />}
                      {u.is_locked && <Chip size="small" label="已锁定" color="error" />}
                      {u.is_active && !u.is_locked && (
                        <Chip size="small" label="正常" color="success" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: '#64748b', fontSize: 12 }}>
                    {u.created_at ? new Date(u.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="编辑">
                      <IconButton size="small" onClick={() => openEdit(u)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="删除">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(u)}
                        disabled={u.id === user.id}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 新建用户对话框 */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>新建用户</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              label="用户名"
              size="small"
              value={createForm.username}
              onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
            />
            <TextField
              label="邮箱"
              size="small"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
            <TextField
              label="密码"
              size="small"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            />
            <TextField
              select
              label="角色"
              size="small"
              value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
            >
              {ROLES.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="初始积分"
              size="small"
              type="number"
              value={createForm.points}
              onChange={(e) => setCreateForm({ ...createForm, points: Number(e.target.value) || 0 })}
            />
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={createForm.is_admin}
                onChange={(e) => setCreateForm({ ...createForm, is_admin: e.target.checked })}
              />
              <Typography variant="body2">设为管理员</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>取消</Button>
          <Button variant="contained" onClick={submitCreate} disabled={creating}>
            {creating ? '创建中...' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 编辑对话框 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>编辑用户</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField
              label="用户名"
              size="small"
              value={editForm.username || ''}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
            />
            <TextField
              label="邮箱"
              size="small"
              value={editForm.email || ''}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
            <TextField
              label="重置密码（留空表示不修改）"
              size="small"
              value={editForm.password || ''}
              onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
            />
            <TextField
              select
              label="角色"
              size="small"
              value={editForm.role || 'free'}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
            >
              {ROLES.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="积分"
              size="small"
              type="number"
              value={editForm.points ?? 0}
              onChange={(e) => setEditForm({ ...editForm, points: Number(e.target.value) || 0 })}
            />
            <Stack direction="row" gap={2} flexWrap="wrap">
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={!!editForm.is_admin}
                  onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })}
                />
                <Typography variant="body2">管理员</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={!!editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                <Typography variant="body2">激活</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={!!editForm.is_locked}
                  onChange={(e) => setEditForm({ ...editForm, is_locked: e.target.checked })}
                />
                <Typography variant="body2">锁定</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={!!editForm.email_verified}
                  onChange={(e) => setEditForm({ ...editForm, email_verified: e.target.checked })}
                />
                <Typography variant="body2">邮箱已验证</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" onClick={submitEdit} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 快捷注册结果 */}
      <Dialog open={!!quickResult} onClose={() => setQuickResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle>快捷注册成功</DialogTitle>
        <DialogContent>
          {quickResult && (
            <Stack gap={1.5} mt={1}>
              <Alert severity="warning">
                密码仅在本次显示，请立即复制保存。
              </Alert>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ minWidth: 70, color: '#64748b' }}>用户名</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {quickResult.username}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(quickResult.username)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ minWidth: 70, color: '#64748b' }}>邮箱</Typography>
                <Typography sx={{ fontFamily: 'monospace' }}>{quickResult.email}</Typography>
                <IconButton size="small" onClick={() => copyToClipboard(quickResult.email)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ minWidth: 70, color: '#64748b' }}>密码</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#dc2626' }}>
                  {quickResult.password}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(quickResult.password)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  copyToClipboard(
                    `用户名: ${quickResult.username}\n邮箱: ${quickResult.email}\n密码: ${quickResult.password}`,
                  )
                }
              >
                一键复制全部
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickResult(null)}>关闭</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snack ? <Alert severity={snack.severity}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default AdminPage;
